import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import moment from "moment";
import * as XLSX from "xlsx";
import { Icon } from "@iconify/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";

import {
  Checkbox,
  IconButton,
  Input,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";

import "./CallHistory.css";
import CallHistoryDetails from "./CallHistoryDetails";
import CallLogFilter from "./CallLogFilter";
import LandingChat from "../Chat/LandingChat";
import Loader from "../Components/Loader";
import formatSecondsToDuration from "../Helper/formatSecondsToDuration";
import formatTime from "../Helper/formatTime";
import useWindowDimensions from "../Hooks/useWindowDimensions";
import { getLogCall, getRecordingFiles } from "../Store/Actions/CallAction";
import { SET_SELECTED_CALL_RECORD_ID } from "../Store/Types/CallTypes";

const CallHistory = ({ isApp, level }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElMore, setAnchorElMore] = useState(null);
  const [callsToExport, setCallsToExport] = useState([]);
  const [data, setData] = useState(null);
  const [filterList, setFilterList] = useState(["", [], [], ""]);
  const [isExportingRecording, setIsExportingRecording] = useState(false);
  const [isExportLogLoading, setIsExportLogLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalShown, setIsModalShown] = useState(false);
  const [page, setPage] = useState(1);
  const [searchContact, setSearchContact] = useState("");
  const [searchContain, setSearchContain] = useState(false);
  const [selected, setSelected] = useState(null);
  const [total, setTotal] = useState(0);
  const dispatch = useDispatch();
  const { agentPermission, myInfo } = useSelector((state) => state.auth);
  const { isInCall, selectedCallRecordID } = useSelector((state) => state.call);
  const { width } = useWindowDimensions();
  const open = Boolean(anchorEl);
  const openMore = Boolean(anchorElMore);
  const userLevel = myInfo.user.Level;

  useEffect(() => {
    fetchData(true, filterList, searchContact);
  }, [isInCall, filterList]);

  useEffect(() => {
    const searchRegex = /^./;
    const result = searchRegex.test(searchContact);
    setSearchContain(result);
  }, [searchContact]);

  const setDateStart = () =>
    filterList[0] === "manual" && filterList[3].datestart
      ? moment(filterList[3].datestart).format("YYYY-MM-DD")
      : filterList[0] === "manual" && !filterList[3].datestart
      ? moment().subtract(1, "days").format("YYYY-MM-DD")
      : filterList[0] === "day"
      ? moment().subtract(0, "days").format("YYYY-MM-DD")
      : filterList[0] === "month"
      ? moment().subtract(1, "months").format("YYYY-MM-DD")
      : moment().subtract(1, "years").format("YYYY-MM-DD");

  const setDateEnd = () =>
    filterList[0] === "manual" && filterList[3].dateend
      ? moment(filterList[3].dateend).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

  const fetchData = (isFirstLoad, filterList, searchContact, page = 1) => {
    if (isFirstLoad) {
      setIsLoading(true);
      setPage(1);
    }

    getLogCall({
      agentlevel: userLevel,
      dateStart: setDateStart(),
      dateEnd: setDateEnd(),
      searchkeyword: searchContact,
      calltype: filterList[1].toString(),
      filteragentids: filterList[2].toString(),
      page: page,
    })
      .then((response) => {
        if (response.status) {
          isFirstLoad
            ? setData(response.data.calls)
            : setData([...data, ...response.data.calls]);

          setTotal(response.data.total);
          selectedCallRecordID && setSelected(selectedCallRecordID);
          dispatch({ type: SET_SELECTED_CALL_RECORD_ID, payload: null });
        } else {
          console.log("error", response.error);
        }

        setIsLoading(false);
      })
      .catch((err) => {
        console.log("err", err);
        setIsLoading(false);
      });
  };

  const handleExportLog = () => {
    setIsExportLogLoading(true);

    getLogCall({
      agentlevel: userLevel,
      dateStart: setDateStart(),
      dateEnd: setDateEnd(),
      searchkeyword: searchContact,
      calltype: filterList[1].toString(),
      filteragentids: filterList[2].toString(),
      page: null,
    })
      .then((response) => {
        if (response.status && response.data) {
          let dataToExport = response.data.calls;

          // Rows to export
          const rows = dataToExport.map((log) => ({
            callTime: moment.utc(log.CallTime).format("DD-MM-yyyy HH:mm:ss"),
            customerName: log.ContactName,
            contactRef: log.ContactRef,
            handleBy: log.AgentName,
            callType: `${log.CallType}${log.IsWABACall ? " WHATSAPP" : ""}`,
            callStatus: log.CallStatus,
            callerNumber: log.CallerNo,
            calledNumber: log.CalledNo,
            connectedNumber: log.ConnectedNo,
            connectTime: moment
              .utc(log.ConnectTime)
              .format("DD-MM-yyyy HH:mm:ss"),

            disconnectTime: moment
              .utc(log.DisconnectTime)
              .format("DD-MM-yyyy HH:mm:ss"),

            duration: formatSecondsToDuration(log.TalkDurationSecond),
            recordingFile: log.RecMessageID[0],
          }));

          // Create worksheet
          const worksheet = XLSX.utils.json_to_sheet(rows);

          // Create workbook
          const workbook = XLSX.utils.book_new();

          // Append worksheet to workbook, and name the worksheet "Call Log"
          XLSX.utils.book_append_sheet(workbook, worksheet, "Call Log");

          // Rename the columns
          XLSX.utils.sheet_add_aoa(
            worksheet,
            [
              [
                "Call Time",
                "Customer Name",
                "Contact Ref",
                "Handle By",
                "Call Type",
                "Call Status",
                "Caller Number",
                "CalledNumber",
                "Connected Number",
                "Connect Time",
                "Disconnect Time",
                "Duration",
                "Recording File",
              ],
            ],

            { origin: "A1" }
          );

          const columns = [
            "callTime",
            "customerName",
            "contactRef",
            "handleBy",
            "callType",
            "callStatus",
            "callerNumber",
            "calledNumber",
            "connectedNumber",
            "connectTime",
            "disconnectTime",
            "duration",
            "recordingFile",
          ];

          // Calculate max width for each column
          const calculateMaxWidth = (column) => {
            return rows.reduce((prev, curr) => {
              if (curr[column]) {
                return Math.max(prev, curr[column].length);
              } else {
                return 10;
              }
            }, 10);
          };

          const maxColumnWidths = columns.map((column) =>
            calculateMaxWidth(column)
          );

          worksheet["!cols"] = maxColumnWidths.map((width) => ({ wch: width }));

          // Write the file and save
          XLSX.writeFile(
            workbook,
            `Call Log ${setDateStart()} - ${setDateEnd()}${
              filterList[1].length ? " - " + filterList[1].toString() : ""
            }.xlsx`,

            { compression: true }
          );

          setIsExportLogLoading(false);
        } else {
          console.log("error", response.error);
          toast.error("Error fetching data", { autoClose: 3000 });
          setIsExportLogLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        toast.error("Error fetching data", { autoClose: 3000 });
        setIsExportLogLoading(false);
      });
  };

  const renderExportMenuItem = () => {
    const exportVoicePermission = agentPermission.find(
      (permission) => permission.PermissionCode === "User.ExportVoice"
    );

    if (
      exportVoicePermission &&
      exportVoicePermission.PermissionAccess === "A"
    ) {
      return (
        <MenuItem
          onClick={() => {
            setIsExportingRecording(true);
            setSelected(null);
          }}
          onMouseEnter={(e) => (e.target.style.backgroundColor = "#ebf0fb")}
          onMouseLeave={(e) => (e.target.style.backgroundColor = "#ffffff")}
        >
          <ListItemIcon>
            <Icon height="26" icon="tabler:package-export" />
          </ListItemIcon>
          Export Recording
        </MenuItem>
      );
    } else {
      return null;
    }
  };

  return (
    <div className="call-history">
      {(width >= 768 || selected === null) && (
        <div className={isApp ? "listInApp" : "listMain"}>
          <div className="list_header_call_history">
            <div className="list_headerLeft">
              <h1>Call Log</h1>
            </div>

            <div className="list_headerRight">
              <Tooltip title="Call Log Menu" arrow>
                <IconButton
                  disabled={isExportingRecording}
                  onClick={(event) => {
                    setAnchorElMore(event.currentTarget);
                  }}
                  aria-controls={open ? "Message_action" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                >
                  <Icon
                    color={isExportingRecording ? "#0343C7" : "#727272"}
                    height="36"
                    icon={
                      isExportingRecording
                        ? "tabler:package-export"
                        : "material-symbols:more-vert"
                    }
                  />
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={anchorElMore}
                className="CallLog-menu"
                id="Message_action"
                open={openMore}
                onClose={() => setAnchorElMore(null)}
                onClick={() => setAnchorElMore(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem
                  disabled={isExportLogLoading}
                  onClick={handleExportLog}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#ebf0fb")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#ffffff")
                  }
                >
                  <ListItemIcon>
                    <Icon height="30" icon="ph:microsoft-excel-logo" />
                  </ListItemIcon>
                  Export Log
                </MenuItem>

                {renderExportMenuItem()}
              </Menu>
            </div>
          </div>

          {isExportingRecording && (
            <div className="selected-call-log">
              <IconButton
                onClick={() => {
                  setCallsToExport([]);
                  setIsExportingRecording(false);
                }}
              >
                <Icon height="28" icon="tabler:x" />
              </IconButton>

              <p>Selected {callsToExport.length}/10 Call Log</p>
            </div>
          )}

          <div className="list_search">
            <div
              className={
                isApp ? "searchbar_containerInApp" : "searchbar_container"
              }
            >
              <SearchOutlined className="search_icon" />

              <Tooltip title="Press 'Enter' to search contact" arrow>
                <Input
                  className="searchbar"
                  placeholder="Search..."
                  disableUnderline={true}
                  type="text"
                  value={searchContact}
                  aria-invalid={searchContain ? "false" : "true"}
                  onChange={(e) => {
                    setSearchContact(e.target.value);

                    // Reset search when empty
                    if (!e.target.value.length) {
                      setSelected(null);
                      fetchData(true, filterList);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setSelected(null);
                      fetchData(true, filterList, e.target.value);
                    }
                  }}
                />
              </Tooltip>

              {searchContain === false ? (
                ""
              ) : (
                <CloseOutlinedIcon
                  className="clear_search_icon"
                  onClick={() => {
                    setSearchContact("");
                    setSelected(null);
                    fetchData(true, filterList);
                  }}
                />
              )}
            </div>

            <div className="single_button">
              <Tooltip title="Advanced Search" arrow>
                <IconButton onClick={() => setIsModalShown(true)}>
                  <Icon
                    color={
                      filterList[0] === "" && !searchContact ? "" : "#0343C7"
                    }
                    height="26"
                    icon="iconamoon:sorting-left-bold"
                  />
                </IconButton>
              </Tooltip>
            </div>

            <CallLogFilter
              agentId={myInfo.user.AgentID}
              isOpen={isModalShown}
              keyword={searchContact}
              level={level}
              setFilterList={setFilterList}
              setIsModalShown={setIsModalShown}
              setKeyword={setSearchContact}
              setSelected={setSelected}
            />
          </div>

          <div className="contact-list" id="infiniteScrollTarget">
            {isLoading ? (
              <Loader />
            ) : (
              <InfiniteScroll
                dataLength={data.length}
                hasMore={data.length < total}
                loader={<Loader />}
                next={() => {
                  fetchData(false, filterList, searchContact, page + 1);
                  setPage(page + 1);
                }}
                scrollableTarget="infiniteScrollTarget"
                scrollThreshold={0.95}
              >
                {!data?.length && <p className="no-data">No data found</p>}

                {data?.map((data) => {
                  return (
                    <div
                      className={`contact-item ${
                        selected === data.RecordID ? "selected" : ""
                      }`}
                      key={data.RecordID}
                      onClick={() => setSelected(data.RecordID)}
                    >
                      {isExportingRecording && (
                        <Checkbox
                          disabled={
                            // Disable unchecked calls when selected calls reached 10 calls
                            !callsToExport.find(
                              (call) => call.RecordID === data.RecordID
                            ) && callsToExport.length >= 10
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Add
                              setCallsToExport([...callsToExport, data]);
                            } else {
                              // Remove
                              setCallsToExport(
                                callsToExport.filter(
                                  (call) => call.RecordID !== data.RecordID
                                )
                              );
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}

                      <div className="contact-info-detail">
                        <div className="contact-info">
                          <p className="contact-name">{data.ContactName}</p>

                          <p className="contact-number">
                            {data.CallType === "OUTBOUND"
                              ? data.CalledNo
                              : data.CallerNo}

                            {data.IsWABACall && " - From WhatsApp"}
                          </p>

                          <div className="agent-data">
                            {userLevel >= 0 && (
                              <div className="contact-by">
                                <Icon
                                  height="18"
                                  icon="material-symbols:support-agent-rounded"
                                  width="18"
                                />

                                <p>{data.AgentName}</p>
                              </div>
                            )}

                            {userLevel >= 0 && data.CampaignName && (
                              <span>-</span>
                            )}

                            {data.CampaignName && (
                              <div className="campaign-name">
                                <Icon
                                  height="18"
                                  icon="material-symbols:flag"
                                  width="18"
                                />

                                <p>{data.CampaignName}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="contact-detail">
                          <div className="detail-status">
                            {data.IsVideoCall ? (
                              <Icon
                                color="#0343c7"
                                height="24"
                                icon="fluent:video-person-call-16-filled"
                                width="24"
                              />
                            ) : data.CallStatus === "ANSWERED" ? (
                              <Icon
                                color="#0343c7"
                                height="24"
                                icon="material-symbols:call"
                                width="24"
                              />
                            ) : (
                              <Icon
                                color="#F97427"
                                height="24"
                                icon="material-symbols:phone-missed"
                                width="24"
                              />
                            )}
                          </div>

                          <p className="contact-time">
                            {formatTime(data.CallTime)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </InfiniteScroll>
            )}
          </div>

          {isExportingRecording && (
            <button
              className="export-button"
              disabled={!callsToExport.length}
              onClick={() => {
                const recordings = [];

                const generateZip = async () => {
                  // Initiate new zip instance
                  let zip = new JSZip();

                  recordings.forEach(async function (recording) {
                    // load each file and add it to the zip file
                    zip.file(recording.filename, recording.filebytes, {
                      base64: true,
                    });
                  });

                  // Generate the zip file then save it
                  zip.generateAsync({ type: "blob" }).then((content) => {
                    saveAs(
                      content,
                      `Call Recordings ${setDateStart()} - ${setDateEnd()}.zip`
                    );
                  });

                  toast.success("Recordings exported successfully", {
                    autoClose: 3000,
                  });
                };

                const getRecordings = (call) => {
                  return new Promise((resolve) => {
                    getRecordingFiles(call.RecMessageID[0])
                      .then((response) => {
                        if (response.status) {
                          recordings.push(response.data);
                          resolve(); // Resolve the promise when the operation is done
                        } else {
                          console.log("error", response.error);

                          toast.error("Error exporting recording", {
                            autoClose: 3000,
                          });
                        }
                      })
                      .catch((err) => {
                        console.log("err", err);
                      });
                  });
                };

                toast.info("Exporting in progress...", {
                  autoClose: 3000,
                });

                // Using Promise.all to wait for all promises to resolve
                Promise.all(callsToExport.map(getRecordings))
                  .then(() => {
                    console.log("Recordings: ", recordings);
                    generateZip();
                  })
                  .catch((error) => {
                    console.error("Error:", error);

                    toast.error("Error exporting recording", {
                      autoClose: 3000,
                    });
                  });

                setCallsToExport([]);
                setIsExportingRecording(false);
              }}
            >
              <Icon height="20" icon="tabler:package-export" />
              Export
            </button>
          )}
        </div>
      )}

      {selected === null ? (
        <LandingChat />
      ) : (
        <CallHistoryDetails
          setSelected={setSelected}
          data={data.find((data) => data.RecordID === selected)}
        />
      )}
    </div>
  );
};

export default CallHistory;
